<template>
  <div class="relative w-full lg:h-[70vh] h-[30vh]">
    <!-- Background Image -->
    <div class="absolute inset-0 overflow-hidden">
      <img
        src="../assets/images/hero-banner.png"
        alt="Background"
        class="w-full h-full object-cover"
      />
      <div class="absolute inset-0 bg-black opacity-50"></div>
    </div>

    <!-- Content Wrapper -->
    <div
      class="relative max-w-screen-4xl mx-auto h-full flex flex-col lg:flex-row items-center justify-center px-6 lg:px-32"
    >
      <h2
        class="text-white text-center text-3xl font-black animate__animated animate__backInLeft"
      >
        Our Services
      </h2>
    </div>
  </div>

  <!-- Main content section -->
  <div class="min-h-screen bg-gray-50">
    <!-- Services Overview Stats -->
    <section class="py-16 bg-white relative">
      <div class="max-w-7xl mx-auto px-4">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div class="group cursor-pointer">
            <div
              class="text-4xl font-black text-yellow-500 mb-2 group-hover:scale-110 transition-transform"
            >
              8
            </div>
            <p class="text-gray-600 font-semibold">Subsidiaries</p>
          </div>
          <div class="group cursor-pointer">
            <div
              class="text-4xl font-black text-yellow-500 mb-2 group-hover:scale-110 transition-transform"
            >
              20+
            </div>
            <p class="text-gray-600 font-semibold">Projects Completed</p>
          </div>
          <div class="group cursor-pointer">
            <div
              class="text-4xl font-black text-yellow-500 mb-2 group-hover:scale-110 transition-transform"
            >
              1000+
            </div>
            <p class="text-gray-600 font-semibold">Happy Clients</p>
          </div>
          <div class="group cursor-pointer">
            <div
              class="text-4xl font-black text-yellow-500 mb-2 group-hover:scale-110 transition-transform"
            >
              6+
            </div>
            <p class="text-gray-600 font-semibold">Years Experience</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Interactive Services Grid -->
    <section ref="servicesSection" class="py-20 bg-main_blue">
      <div class="max-w-7xl mx-auto px-4">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <h2 class="text-5xl font-black text-white mb-6">
            Choose Your <span class="text-yellow">Solution</span>
          </h2>
          <p class="text-xl text-white max-w-3xl mx-auto">
            Click on any service to discover how we can transform your business
            with our specialized expertise
          </p>
        </div>

        <!-- Services Filter Tabs -->
        <div class="flex flex-wrap justify-center gap-4 mb-12">
          <button
            v-for="category in categories"
            :key="category"
            @click="activeCategory = category"
            :class="[
              'px-6 py-3 rounded-full font-semibold transition-all duration-300',
              activeCategory === category
                ? 'bg-yellow text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-yellow',
            ]"
          >
            {{ category }}
          </button>
        </div>

        <!-- Dynamic Services Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          <template
            v-for="(service, index) in filteredServices"
            :key="service.name"
          >
            <div
              @click="selectService(service)"
              :class="[
                'group relative bg-white rounded-3xl p-8 cursor-pointer transition-all duration-500 hover:shadow-2xl border-2',
                selectedService?.name === service.name
                  ? 'border-yellow-500 shadow-2xl transform scale-105'
                  : 'border-transparent hover:border-yellow-200',
              ]"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
            >
              <!-- Service Icon -->
              <div class="relative mb-6">
                <div
                  :class="[
                    'w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300',
                    selectedService?.name === service.name
                      ? 'bg-yellow shadow-lg'
                      : 'bg-gradient-to-br from-yellow- to-yellow group-hover:from-yellow group-hover:to-yellow',
                  ]"
                >
                  <img
                    :src="service.image"
                    :alt="service.name"
                    class="w-12 h-12 object-contain transition-all duration-300"
                  />
                </div>
                <!-- Selection Indicator -->
                <div
                  v-if="selectedService?.name === service.name"
                  class="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </div>
              </div>

              <!-- Service Content -->
              <h3
                class="text-2xl font-bold text-gray-800 mb-3 group-hover:text-yellow-600 transition-colors"
              >
                {{ service.name }}
              </h3>
              <p class="text-gray-600 leading-relaxed mb-4 line-clamp-3">
                {{ service.shortDescription }}
              </p>

              <!-- Service Tags -->
              <div class="flex flex-wrap gap-2 mb-4">
                <span
                  v-for="tag in service.tags?.slice(0, 2)"
                  :key="tag"
                  class="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full font-medium"
                >
                  {{ tag }}
                </span>
              </div>

              <!-- Hover Arrow -->
              <div
                class="flex items-center text-yellow-500 font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Learn More
                <svg
                  class="w-4 h-4 ml-2 transform group-hover:translate-x-2 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </div>
            </div>
          </template>
        </div>

        <!-- Scroll Indicator (when service is selected) -->
        <div v-if="selectedService" class="text-center mt-12 animate-bounce">
          <div class="text-white text-sm mb-2">Scroll down to see details</div>
          <svg
            class="w-6 h-6 text-yellow mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            ></path>
          </svg>
        </div>
      </div>
    </section>

    <!-- Selected Service Detail Modal/Section -->
    <section
      v-if="selectedService"
      ref="serviceDetailSection"
      class="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 transition-all duration-700 ease-in-out"
      :class="{ 'animate-fade-in-up': selectedService }"
    >
      <div class="max-w-7xl mx-auto px-4">
        <!-- Back to Services Button (Sticky) -->
        <div class="sticky top-4 z-10 mb-8">
          <button
            @click="closeServiceDetail"
            class="inline-flex items-center bg-white hover:bg-yellow-50 text-gray-700 hover:text-yellow-600 px-4 py-2 rounded-full shadow-lg border-2 border-transparent hover:border-yellow-200 transition-all duration-300 font-semibold"
          >
            <svg
              class="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              ></path>
            </svg>
            Back to Services
          </button>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <!-- Service Details -->
          <div class="animate-slide-in-left">
            <div class="flex items-center mb-6">
              <img
                :src="selectedService.image"
                :alt="selectedService.name"
                class="w-16 h-16 mr-4"
              />
              <div>
                <h3 class="text-4xl font-black text-gray-800">
                  {{ selectedService.name }}
                </h3>
                <p class="text-yellow-500 font-semibold">
                  {{ selectedService.category }}
                </p>
              </div>
            </div>

            <p class="text-lg text-gray-600 leading-relaxed mb-8">
              {{ selectedService.description }}
            </p>

            <!-- Key Features -->
            <div class="space-y-4 mb-8">
              <h4 class="text-xl font-bold text-gray-800">Key Features:</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div
                  v-for="(feature, index) in selectedService.features"
                  :key="feature"
                  class="flex items-center animate-fade-in"
                  :style="{ 'animation-delay': `${index * 0.1}s` }"
                >
                  <svg
                    class="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                  <span class="text-gray-700">{{ feature }}</span>
                </div>
              </div>
            </div>

            <!-- CTA Buttons -->
            <div class="flex flex-col sm:flex-row gap-4">
              <button
                class="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-4 rounded-lg font-bold transition-all duration-300 hover:shadow-lg hover:scale-105"
              >
                Get Quote
              </button>
              <button
                @click="closeServiceDetail"
                class="border-2 border-gray-300 hover:border-yellow-500 text-gray-700 hover:text-yellow-600 px-8 py-4 rounded-lg font-bold transition-all duration-300"
              >
                View All Services
              </button>
            </div>
          </div>

          <!-- Visual Element -->
          <div class="relative animate-slide-in-right">
            <div
              class="bg-gradient-to-br from-yellow to-yellow rounded-3xl p-12 text-white text-center shadow-2xl hover:shadow-3xl transition-all duration-300"
            >
              <div class="text-6xl font-black mb-4 animate-pulse">
                {{
                  selectedService.name.split(" ")[1] ||
                  selectedService.name.split(" ")[0]
                }}
              </div>
              <p class="text-xl opacity-90 mb-6">Professional Excellence</p>
              <div class="flex justify-center space-x-8">
                <div class="text-center">
                  <div class="text-2xl font-bold">24/7</div>
                  <div class="text-sm opacity-75">Support</div>
                </div>
                <div class="text-center">
                  <div class="text-2xl font-bold">100%</div>
                  <div class="text-sm opacity-75">Quality</div>
                </div>
                <div class="text-center">
                  <div class="text-2xl font-bold">Fast</div>
                  <div class="text-sm opacity-75">Delivery</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact CTA -->
    <section class="py-20 bg-white text-black">
      <div class="max-w-4xl mx-auto text-center px-4">
        <h2 class="text-4xl font-black mb-6">Ready to Start Your Project?</h2>
        <p class="text-xl mb-8 opacity-90">
          Let's discuss how our comprehensive services can help achieve your
          goals
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            class="bg-yellow hover:bg-yellow-400 text-black px-8 py-4 rounded-full font-bold transition-all duration-300 hover:shadow-lg transform hover:scale-105"
          >
            Start Conversation
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "ModernServicesPage",
  data() {
    return {
      selectedService: null,
      activeCategory: "All",
      categories: [
        "All",
        "Construction",
        "Business",
        "Hospitality",
        "Community",
      ],
      services: [
        {
          name: "Mshel Homes",
          category: "Construction",
          shortDescription:
            "Premium housing solutions tailored to modern lifestyles with sustainable design principles.",
          description:
            "We provide comprehensive housing solutions tailored to modern lifestyles, offering everything from residential development to property management services that prioritize comfort, sustainability, and community living. Our team combines innovative design with practical functionality to create homes that enhance quality of life.",
          image: require("../assets/images/house.png"),
          features: [
            "Residential Development",
            "Property Management",
            "Sustainable Design",
            "Smart Home Integration",
            "Community Planning",
          ],
          tags: ["Residential", "Sustainable", "Modern"],
        },
        {
          name: "Lisa Suites",
          category: "Hospitality",
          shortDescription:
            "Premier serviced apartments offering luxurious accommodations with personalized service.",
          description:
            "We are a premier serviced apartment company offering luxurious home-like accommodations with personalized service, perfect for business travelers, extended stays, and those seeking premium temporary housing solutions. Our suites combine the comfort of home with the luxury of a five-star hotel.",
          image: require("../assets/images/building.png"),
          features: [
            "Luxury Apartments",
            "Concierge Service",
            "Extended Stays",
            "Business Facilities",
            "Premium Amenities",
          ],
          tags: ["Luxury", "Hospitality", "Service"],
        },
        {
          name: "Mshel Prints",
          category: "Business",
          shortDescription:
            "Premium printing solutions with expert craftsmanship for impactful brand presence.",
          description:
            "We are your one-stop shop for premium flex banners, SAVs, brochures, business cards and many more with expert craftsmanship to deliver vibrant, high-impact prints that elevate your brand presence. Our state-of-the-art printing technology ensures exceptional quality every time.",
          image: require("../assets/images/offset.png"),
          features: [
            "Digital Printing",
            "Brand Design",
            "Marketing Materials",
            "Large Format Printing",
            "Custom Solutions",
          ],
          tags: ["Printing", "Branding", "Marketing"],
        },
        {
          name: "Mshel Construction",
          category: "Construction",
          shortDescription:
            "Innovative construction services delivering quality and durability across all project types.",
          description:
            "Innovative construction services delivering quality and durability across residential, commercial, and industrial projects. We combine traditional craftsmanship with modern technology for exceptional results that stand the test of time.",
          image: require("../assets/images/hook.png"),
          features: [
            "Residential Build",
            "Commercial Projects",
            "Renovation",
            "Project Management",
            "Quality Assurance",
          ],
          tags: ["Construction", "Quality", "Innovation"],
        },
        {
          name: "Mshel Blocks & Machineries",
          category: "Construction",
          shortDescription:
            "Top-quality block production and machinery services for construction projects.",
          description:
            "Top-notch block production and machinery services providing high-quality building materials and equipment rental solutions for construction projects of all scales. We ensure consistent quality and reliable supply chains for your construction needs.",
          image: require("../assets/images/construction (1).png"),
          features: [
            "Block Production",
            "Equipment Rental",
            "Quality Materials",
            "Supply Chain",
            "Technical Support",
          ],
          tags: ["Manufacturing", "Equipment", "Materials"],
        },
        {
          name: "Mshel Consult",
          category: "Business",
          shortDescription:
            "Expert consulting services to optimize business strategy and operational efficiency.",
          description:
            "Expert consulting services across various sectors including business strategy, project management, and operational optimization to help organizations achieve sustainable growth and efficiency. We provide data-driven insights and actionable strategies.",
          image: require("../assets/images/consulting.png"),
          features: [
            "Business Strategy",
            "Project Management",
            "Operational Excellence",
            "Data Analytics",
            "Change Management",
          ],
          tags: ["Consulting", "Strategy", "Optimization"],
        },
        {
          name: "Mshel Logistics",
          category: "Business",
          shortDescription:
            "Comprehensive logistics and supply chain solutions for seamless operations.",
          description:
            "Efficient logistics and supply chain solutions providing seamless transportation, warehousing, and distribution services to keep your business operations running smoothly. We leverage technology to optimize routes and reduce costs.",
          image: require("../assets/images/delivery-location.png"),
          features: [
            "Supply Chain",
            "Warehousing",
            "Transportation",
            "Route Optimization",
            "Inventory Management",
          ],
          tags: ["Logistics", "Supply Chain", "Efficiency"],
        },
        {
          name: "Mshel Foundation",
          category: "Community",
          shortDescription:
            "Empowering communities through impactful social development initiatives.",
          description:
            "Empowering communities through impactful initiatives focused on education, healthcare, and sustainable development to create lasting positive change in society. We believe in giving back and building stronger communities for future generations.",
          image: require("../assets/images/generous.png"),
          features: [
            "Community Development",
            "Education Programs",
            "Healthcare Initiatives",
            "Sustainable Development",
            "Social Impact",
          ],
          tags: ["Community", "Education", "Healthcare"],
        },
      ],
    };
  },
  computed: {
    filteredServices() {
      if (this.activeCategory === "All") {
        return this.services;
      }
      return this.services.filter(
        (service) => service.category === this.activeCategory
      );
    },
  },

  methods: {
    // Enhanced selectService method with smooth scrolling
    async selectService(service) {
      // Toggle service selection (allow deselection)
      this.selectedService =
        this.selectedService?.name === service.name ? null : service;

      // If a service is selected, scroll to it
      if (this.selectedService) {
        // Wait for DOM update
        await this.$nextTick();

        // Smooth scroll to service detail section
        this.scrollToServiceDetail();

        // Optional: Add visual feedback
        this.showSelectionFeedback();
      }
    },

    // Smooth scroll to service detail section
    scrollToServiceDetail() {
      const serviceDetailSection = this.$refs.serviceDetailSection;

      if (serviceDetailSection) {
        // Calculate offset to show some spacing from top
        const offset = 100;
        const elementPosition =
          serviceDetailSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        // Smooth scroll with easing
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        // Alternative method using element.scrollIntoView
        // serviceDetailSection.scrollIntoView({
        //   behavior: 'smooth',
        //   block: 'start',
        //   inline: 'nearest'
        // });
      }
    },

    // Enhanced close service detail with smooth scroll back
    closeServiceDetail() {
      // Smooth scroll back to services section
      this.scrollToServices();

      // Delay clearing selection to allow scroll animation
      setTimeout(() => {
        this.selectedService = null;
      }, 300);
    },

    // Original scrollToServices method (maintained compatibility)
    scrollToServices() {
      const servicesSection = this.$refs.servicesSection;

      if (servicesSection) {
        servicesSection.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    },

    // Visual feedback when service is selected
    showSelectionFeedback() {
      // Create a temporary toast notification
      const toast = document.createElement("div");
      toast.innerHTML = `
        <div class="fixed top-6 right-6 bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl z-50 transform transition-all duration-500 ease-out translate-x-0 animate-slide-in">
  <div class="flex items-center space-x-3">
    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
    </svg>
    <span class="font-medium tracking-wide">Service selected! Scrolling to details...</span>
  </div>
</div>
      `;

      document.body.appendChild(toast);

      // Animate in
      setTimeout(() => {
        toast.firstElementChild.classList.remove("translate-x-full");
      }, 100);

      // Remove after 3 seconds
      setTimeout(() => {
        toast.firstElementChild.classList.add("translate-x-full");
        setTimeout(() => {
          document.body.removeChild(toast);
        }, 300);
      }, 3000);
    },

    // Alternative method: Scroll with custom easing
    smoothScrollTo(element, duration = 800) {
      const targetPosition =
        element.getBoundingClientRect().top + window.pageYOffset - 100;
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      let startTime = null;

      // Easing function
      const easeInOutQuart = (t) => {
        return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
      };

      const animation = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const run =
          easeInOutQuart(timeElapsed / duration) * distance + startPosition;

        window.scrollTo(0, run);

        if (timeElapsed < duration) {
          requestAnimationFrame(animation);
        }
      };

      requestAnimationFrame(animation);
    },

    // Handle keyboard navigation
    handleKeyPress(event) {
      if (event.key === "Escape" && this.selectedService) {
        this.closeServiceDetail();
      }
    },
  },

  // Add keyboard event listener
  mounted() {
    document.addEventListener("keydown", this.handleKeyPress);
  },

  // Clean up event listener
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleKeyPress);
  },

  // Watch for service selection to trigger scroll
  watch: {
    selectedService(newService, oldService) {
      if (newService && !oldService) {
        // Service newly selected
        this.$nextTick(() => {
          this.scrollToServiceDetail();
        });
      }
    },
  },
};
</script>

<style scoped>
/* Line clamp utility */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Custom animations */
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

/* Smooth transitions */
* {
  transition: all 0.3s ease;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #eab308;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #ca8a04;
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-in-left {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slide-in-right {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.7s ease-out;
}

.animate-slide-in-left {
  animation: slide-in-left 0.6s ease-out;
}

.animate-slide-in-right {
  animation: slide-in-right 0.6s ease-out;
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out;
}
</style>
